import { useEffect, useState } from "react";
import {
  useCreateNewsLabel,
  useCreateNewsPost,
  useDeleteNewsLabel,
  useDeleteNewsPost,
  useNewsLabels,
  useNewsPost,
  useNewsPosts,
  useUpdateNewsPost,
} from "@/hooks/useNewsroom";
import { NewsMediaItem, NewsPost, NewsPostInput } from "@/types/news";
import { Author } from "@/types/authors";
import { useAuthors } from "@/hooks/useAuthors";
import AuthorSelector from "@/components/AuthorSelector";
import ImageUploader from "@/components/ImageUploader";
import MarkdownEditor from "@/components/MarkdownEditor";
import { Button, PageHeader } from "@/components/ui";
import { Icon } from "@/components/IconSprite";
import { clsx } from "clsx";

interface PostFormState {
  title: string;
  summary: string;
  content: string;
  heroImageUrl: string;
  authorId: string | null;
  authorName: string;
  authorTitle: string;
  authorAvatarUrl: string;
  audience: string;
  ctaLabel: string;
  ctaUrl: string;
  isPublished: boolean;
  publishedAt: string | null;
  slug: string;
  labelIds: string[];
  media: Array<{ url: string; caption: string; altText: string }>;
}

const defaultPostForm = (): PostFormState => ({
  title: "",
  summary: "",
  content: "",
  heroImageUrl: "",
  authorId: null,
  authorName: "",
  authorTitle: "",
  authorAvatarUrl: "",
  audience: "",
  ctaLabel: "",
  ctaUrl: "",
  isPublished: false,
  publishedAt: null,
  slug: "",
  labelIds: [],
  media: [],
});

const toDatetimeLocal = (iso: string | null): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16);
};

const mapPostToForm = (post: NewsPost, authors?: Author[]): PostFormState => {
  // Try to find author by name match if we have authors data
  const matchedAuthor = authors?.find(
    (a) => a.displayName === post.authorName && a.title === post.authorTitle
  );
  return {
    title: post.title,
    summary: post.summary ?? "",
    content: post.content ?? "",
    heroImageUrl: post.heroImageUrl ?? "",
    authorId: matchedAuthor?.authorId ?? null,
    authorName: post.authorName ?? "",
    authorTitle: post.authorTitle ?? "",
    authorAvatarUrl: post.authorAvatarUrl ?? "",
    audience: post.audience ?? "",
    ctaLabel: post.ctaLabel ?? "",
    ctaUrl: post.ctaUrl ?? "",
    isPublished: post.isPublished,
    publishedAt: post.publishedAt ?? null,
    slug: post.slug ?? "",
    labelIds: post.labels.map((l) => l.labelId),
    media: post.media.map((m) => ({ url: m.url, caption: m.caption ?? "", altText: m.altText ?? "" })),
  };
};

const sanitizePostPayload = (form: PostFormState): NewsPostInput => ({
  title: form.title.trim(),
  summary: form.summary.trim() || null,
  content: form.content,
  heroImageUrl: form.heroImageUrl.trim() || null,
  authorName: form.authorName.trim() || null,
  authorTitle: form.authorTitle.trim() || null,
  authorAvatarUrl: form.authorAvatarUrl.trim() || null,
  audience: form.audience.trim() || null,
  ctaLabel: form.ctaLabel.trim() || null,
  ctaUrl: form.ctaUrl.trim() || null,
  isPublished: form.isPublished,
  publishedAt: form.publishedAt,
  slug: form.slug.trim() || null,
  labelIds: form.labelIds,
  media: form.media
    .filter((m) => m.url.trim().length > 0)
    .map<NewsMediaItem>((m) => ({
      type: "image",
      url: m.url.trim(),
      caption: m.caption.trim() || null,
      altText: m.altText.trim() || null,
    })),
});

const Newsroom = () => {
  const { data: posts, isLoading: postsLoading } = useNewsPosts();
  const { data: labels } = useNewsLabels();
  const { data: authors } = useAuthors();
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [creatingPost, setCreatingPost] = useState(false);
  const { data: postDetail } = useNewsPost(!creatingPost ? selectedPostId : null);
  const [postForm, setPostForm] = useState<PostFormState>(defaultPostForm);

  const createLabel = useCreateNewsLabel();
  const deleteLabel = useDeleteNewsLabel();
  const createPost = useCreateNewsPost();
  const updatePost = useUpdateNewsPost();
  const deletePost = useDeleteNewsPost();

  const [newLabelName, setNewLabelName] = useState("");

  useEffect(() => {
    if (!postsLoading && posts?.length && !selectedPostId && !creatingPost) {
      setSelectedPostId(posts[0].postId);
    }
  }, [posts, postsLoading, selectedPostId, creatingPost]);

  useEffect(() => {
    if (creatingPost) {
      setPostForm(defaultPostForm());
      return;
    }
    if (postDetail) {
      setPostForm(mapPostToForm(postDetail, authors));
    } else if (!selectedPostId) {
      setPostForm(defaultPostForm());
    }
  }, [postDetail, creatingPost, selectedPostId, authors]);

  const handleSelectPost = (postId: string) => {
    setCreatingPost(false);
    setSelectedPostId(postId);
  };

  const handleStartNewPost = () => {
    setCreatingPost(true);
    setSelectedPostId(null);
    setPostForm(defaultPostForm());
  };

  const handleSavePost = () => {
    const payload = sanitizePostPayload(postForm);
    if (!payload.title || !payload.content) return;
    if (creatingPost) {
      createPost.mutate(payload, {
        onSuccess: (post) => {
          setCreatingPost(false);
          setSelectedPostId(post.postId);
        },
      });
    } else if (selectedPostId) {
      updatePost.mutate({ postId: selectedPostId, payload });
    }
  };

  const handleDeletePost = () => {
    if (!selectedPostId) return;
    deletePost.mutate(selectedPostId, {
      onSuccess: () => {
        setSelectedPostId(null);
        setCreatingPost(false);
        setPostForm(defaultPostForm());
      },
    });
  };

  const handleCreateLabel = () => {
    if (!newLabelName.trim()) return;
    createLabel.mutate({ name: newLabelName.trim() }, { onSuccess: () => setNewLabelName("") });
  };

  const toggleLabel = (labelId: string) => {
    setPostForm((prev) => ({
      ...prev,
      labelIds: prev.labelIds.includes(labelId)
        ? prev.labelIds.filter((id) => id !== labelId)
        : [...prev.labelIds, labelId],
    }));
  };

  const addMediaItem = () => {
    setPostForm((prev) => ({
      ...prev,
      media: [...prev.media, { url: "", caption: "", altText: "" }],
    }));
  };

  const updateMediaItem = (index: number, field: keyof PostFormState["media"][number], value: string) => {
    setPostForm((prev) => {
      const next = [...prev.media];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, media: next };
    });
  };

  const removeMediaItem = (index: number) => {
    setPostForm((prev) => ({ ...prev, media: prev.media.filter((_, i) => i !== index) }));
  };

  const handleAuthorChange = (author: Author | null) => {
    setPostForm((prev) => ({
      ...prev,
      authorId: author?.authorId ?? null,
      authorName: author?.displayName ?? "",
      authorTitle: author?.title ?? "",
      authorAvatarUrl: author?.avatarUrl ?? "",
    }));
  };

  const busy = createPost.isPending || updatePost.isPending || deletePost.isPending;
  const showForm = creatingPost || Boolean(selectedPostId);
  const formTitle = creatingPost ? "New Post" : postDetail?.title ?? "Select a post";

  return (
    <div className="page wide">
      <PageHeader
        label="Content"
        title="Newsroom"
        description="Manage updates and announcements shown in the assistant's News tab."
      />

      <div className="md a">
        {/* Left column: Posts + Labels */}
        <div className="stack">
          {/* POSTS */}
          <div className="card">
            <div className="card-h tight">
              <div>
                <h2>Posts</h2>
              </div>
              <div className="r">
                <button type="button" className="btn out sm" onClick={handleStartNewPost}>
                  <Icon name="plus" />
                  New
                </button>
              </div>
            </div>
            <div className="list-scroll" style={{ maxHeight: 340 }}>
              {postsLoading && <p className="meta" style={{ padding: "12px 14px" }}>Loading...</p>}
              {posts?.map((post) => (
                <button
                  key={post.postId}
                  type="button"
                  onClick={() => handleSelectPost(post.postId)}
                  className={clsx("list-i", !creatingPost && selectedPostId === post.postId && "on")}
                >
                  <span className="tx">
                    <b>{post.title}</b>
                    <span style={{ display: "flex", gap: 5, marginTop: 4, alignItems: "center" }}>
                      <span className={clsx("b", post.isPublished ? "suc" : "def")}>
                        {post.isPublished ? "Live" : "Draft"}
                      </span>
                    </span>
                  </span>
                </button>
              ))}
              {posts && !posts.length && !postsLoading && (
                <p className="meta" style={{ padding: "12px 14px" }}>No posts yet.</p>
              )}
            </div>
          </div>

          {/* LABELS */}
          <div className="card">
            <div className="card-h tight">
              <div>
                <h2>Labels</h2>
              </div>
            </div>
            <div className="card-b">
              <div style={{ display: "flex", gap: 7, marginBottom: 12 }}>
                <input
                  className="inp"
                  value={newLabelName}
                  onChange={(e) => setNewLabelName(e.target.value)}
                  placeholder="Add label"
                  style={{ height: 30, fontSize: 12.5 }}
                />
                <Button variant="secondary" size="sm" onClick={handleCreateLabel}>
                  Create
                </Button>
              </div>
              <div className="chips">
                {labels?.map((label) => (
                  <span key={label.labelId} className="chip">
                    {label.name}
                    <button
                      type="button"
                      className="x"
                      onClick={() => deleteLabel.mutate(label.labelId)}
                      title="Remove label"
                    >
                      <Icon name="x" />
                    </button>
                  </span>
                ))}
                {labels && !labels.length && <p className="meta">No labels yet.</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Right column: Post form */}
        <div className="card">
          <div className="card-h">
            <div>
              <h2>
                {formTitle}
                {showForm && !creatingPost && (
                  <span className={clsx("b", postForm.isPublished ? "suc" : "def")}>
                    <span className="dot"></span>
                    {postForm.isPublished ? "Live" : "Draft"}
                  </span>
                )}
              </h2>
            </div>
            <div className="r">
              {!creatingPost && selectedPostId && (
                <Button variant="danger" size="sm" onClick={handleDeletePost}>
                  <Icon name="trash" />
                  Delete
                </Button>
              )}
              <Button variant="primary" size="sm" onClick={handleSavePost} loading={busy}>
                Save
              </Button>
            </div>
          </div>
          <div className="card-b">
            <div className="row c2">
              <div className="fg">
                <label className="fl">
                  Title <span className="req">*</span>
                </label>
                <input
                  className="inp"
                  value={postForm.title}
                  onChange={(e) => setPostForm((p) => ({ ...p, title: e.target.value }))}
                />
              </div>
              <div className="fg">
                <label className="fl">
                  Slug <span className="req">*</span>
                </label>
                <input
                  className="inp mono"
                  value={postForm.slug}
                  onChange={(e) => setPostForm((p) => ({ ...p, slug: e.target.value }))}
                />
              </div>
            </div>

            <div className="fg">
              <label className="fl">Summary</label>
              <textarea
                className="ta"
                rows={2}
                value={postForm.summary}
                onChange={(e) => setPostForm((p) => ({ ...p, summary: e.target.value }))}
              />
            </div>

            <div className="fg">
              <div className="lbl-row">
                <label className="fl">Content</label>
                <span className="cnt">Markdown</span>
              </div>
              <MarkdownEditor
                value={postForm.content}
                onChange={(value) => setPostForm((p) => ({ ...p, content: value }))}
                placeholder="Write your news post content here..."
                height={350}
              />
            </div>

            <div className="row" style={{ gridTemplateColumns: "1fr 200px" }}>
              <div className="fg">
                <label className="fl">Hero Image</label>
                <ImageUploader
                  value={postForm.heroImageUrl || null}
                  onChange={(url) => setPostForm((p) => ({ ...p, heroImageUrl: url || "" }))}
                  placeholder="Drop image or browse library"
                />
              </div>
              <div className="fg">
                <label className="fl">Author</label>
                <AuthorSelector
                  value={postForm.authorId}
                  onChange={handleAuthorChange}
                  placeholder="Select an author"
                />
              </div>
            </div>

            <div className="row c3">
              <div className="fg">
                <label className="fl">Audience</label>
                <input
                  className="inp"
                  value={postForm.audience}
                  onChange={(e) => setPostForm((p) => ({ ...p, audience: e.target.value }))}
                  placeholder="e.g. Leads, Members"
                />
              </div>
              <div className="fg">
                <label className="fl">CTA Label</label>
                <input
                  className="inp"
                  value={postForm.ctaLabel}
                  onChange={(e) => setPostForm((p) => ({ ...p, ctaLabel: e.target.value }))}
                />
              </div>
              <div className="fg">
                <label className="fl">CTA URL</label>
                <input
                  className="inp mono"
                  value={postForm.ctaUrl}
                  onChange={(e) => setPostForm((p) => ({ ...p, ctaUrl: e.target.value }))}
                />
              </div>
            </div>

            <div className="row c2">
              <div className="fg">
                <label className="fl">Published At</label>
                <input
                  className="inp"
                  type="datetime-local"
                  value={toDatetimeLocal(postForm.publishedAt)}
                  onChange={(e) =>
                    setPostForm((p) => ({
                      ...p,
                      publishedAt: e.target.value ? new Date(e.target.value).toISOString() : null,
                    }))
                  }
                />
              </div>
              <div className="fg">
                <label className="fl">Published</label>
                <div style={{ display: "flex", alignItems: "center", gap: 9, height: 34 }}>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={postForm.isPublished}
                    className={clsx("tg", postForm.isPublished && "on")}
                    onClick={() => setPostForm((p) => ({ ...p, isPublished: !p.isPublished }))}
                  />
                  <span style={{ fontSize: 12.5, color: "var(--ink-3)", fontWeight: 560 }}>
                    Live in News tab
                  </span>
                </div>
              </div>
            </div>

            {/* Labels selection */}
            <div className="sec">
              <div className="sec-h">
                <b>Labels</b>
                <span>Used to filter the News tab.</span>
              </div>
              <div className="chips">
                {labels?.map((label) => (
                  <button
                    key={label.labelId}
                    type="button"
                    className={clsx("chip", postForm.labelIds.includes(label.labelId) && "on")}
                    onClick={() => toggleLabel(label.labelId)}
                  >
                    {label.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Media */}
            <div className="sec">
              <div className="sec-h" style={{ display: "flex", alignItems: "center" }}>
                <div>
                  <b>Media</b>
                  <span>Images shown in the post gallery.</span>
                </div>
                <button
                  type="button"
                  className="btn sec sm"
                  style={{ marginLeft: "auto" }}
                  onClick={addMediaItem}
                >
                  <Icon name="plus" />
                  Add
                </button>
              </div>
              {postForm.media.length === 0 && <p className="meta">No media items.</p>}
              {postForm.media.map((item, i) => (
                <div key={i} className="subtle-box" style={{ marginBottom: 8 }}>
                  <div className="row c3" style={{ gap: 10 }}>
                    <div>
                      <label className="fl">URL</label>
                      <input
                        className="inp mono"
                        value={item.url}
                        onChange={(e) => updateMediaItem(i, "url", e.target.value)}
                        placeholder="URL"
                        style={{ height: 30, fontSize: 12 }}
                      />
                    </div>
                    <div>
                      <label className="fl">Caption</label>
                      <input
                        className="inp"
                        value={item.caption}
                        onChange={(e) => updateMediaItem(i, "caption", e.target.value)}
                        placeholder="Caption"
                        style={{ height: 30, fontSize: 12 }}
                      />
                    </div>
                    <div style={{ display: "flex", gap: 7, alignItems: "flex-end" }}>
                      <div style={{ flex: 1 }}>
                        <label className="fl">Alt text</label>
                        <input
                          className="inp"
                          value={item.altText}
                          onChange={(e) => updateMediaItem(i, "altText", e.target.value)}
                          placeholder="Alt text"
                          style={{ height: 30, fontSize: 12 }}
                        />
                      </div>
                      <button
                        type="button"
                        className="act d"
                        style={{ height: 30, width: 30 }}
                        onClick={() => removeMediaItem(i)}
                        title="Remove"
                      >
                        <Icon name="trash" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Newsroom;
